import { motion } from "framer-motion";
import { ArrowRight, XCircle, Sparkles } from "lucide-react";

const OLD_PATH = ["STUDY", "GET A JOB", "WORK", "RETIRE"];
const NEW_PATH = ["IDEA", "BUILD", "LAUNCH", "CREATE JOBS", "GROW", "TRANSFORM"];

const MISSION_ITEMS = [
  "Business strategy",
  "Market research",
  "Branding",
  "Technology",
  "Product development",
  "Marketing",
  "Sales",
  "Operations",
  "Digital systems",
  "Growth support",
];

function PathRow({ steps, glowing, testid }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-2.5" data-testid={testid}>
      {steps.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <span
            className={`px-3.5 py-2 rounded-lg font-display font-semibold text-xs sm:text-sm tracking-wide ${
              glowing
                ? "glass text-white border border-[#FF8800]/40 shadow-[0_0_18px_rgba(255,136,0,0.12)]"
                : "bg-white/[0.03] border border-white/10 text-slate-500"
            }`}
          >
            {s}
          </span>
          {i < steps.length - 1 && (
            <ArrowRight className={`w-3.5 h-3.5 ${glowing ? "text-[#FF8800]" : "text-slate-700"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

export function CoreVision() {
  return (
    <section id="vision-bigger" data-testid="vision-bigger-section" className="relative py-20 sm:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">
        {/* The Bigger Vision */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-4">The Bigger Vision</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white max-w-3xl" data-testid="vision-bigger-headline">
            INDIA DESERVES <span className="text-gradient-saffron">ANOTHER PATH.</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-3xl">
            India has one of the world's largest young populations and enormous entrepreneurial
            potential. But millions of people still believe the only path to success is:
          </p>

          <div className="mt-10 space-y-8 max-w-4xl">
            <div className="glass rounded-2xl p-6 sm:p-8 opacity-80">
              <p className="font-mono-tech text-[10px] tracking-[0.3em] text-slate-500 uppercase mb-4 flex items-center gap-2">
                <XCircle className="w-3.5 h-3.5" /> The only path most people see
              </p>
              <PathRow steps={OLD_PATH} glowing={false} testid="vision-path-old" />
            </div>
            <div className="glass rounded-2xl p-6 sm:p-8 border border-[#FF8800]/25 shadow-[0_0_50px_rgba(255,136,0,0.08)]">
              <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#FF8800] uppercase mb-4 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> The path LaunchLab is building
              </p>
              <PathRow steps={NEW_PATH} glowing testid="vision-path-new" />
            </div>
          </div>

          <p className="mt-10 text-lg sm:text-xl text-slate-200 leading-relaxed max-w-3xl font-display" data-testid="vision-bigger-statement">
            The vision is not simply to create more companies.
            <br />
            <span className="text-gradient-india font-bold">
              The vision is to create more people who create companies.
            </span>
          </p>
        </motion.div>

        {/* Mission */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          data-testid="vision-mission"
        >
          <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-4">LaunchLab's Mission</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white max-w-3xl" data-testid="vision-mission-headline">
            ENTREPRENEURSHIP, <span className="text-gradient-saffron">MADE ACCESSIBLE.</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-3xl">
            An entrepreneur should not have to search separately for:
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-5xl" data-testid="vision-mission-chips">
            {MISSION_ITEMS.map((m, i) => (
              <motion.div
                key={m}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                className="glass rounded-xl px-4 py-3.5 text-sm text-slate-200 text-center hover:border-[#00B0FF]/40 transition-colors"
              >
                {m}
              </motion.div>
            ))}
          </div>
          <p className="mt-8 text-lg text-slate-200 font-display max-w-3xl">
            LaunchLab brings these capabilities into{" "}
            <span className="text-gradient-saffron font-bold">one connected ecosystem.</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
}
